/**
 * useSampleQuestions Hook
 * 
 * Manages sample questions generation state and logic
 * Handles fetching, caching, and form change detection
 */

import { useState, useEffect } from "react";
import { message, FormInstance } from "antd";
import { InterviewRound } from "../../../../../../../Dto/GenerateJobDescription.dto";
import { SampleQuestion } from "../../../../../../../Interface";
import { Requirements } from "../../../../../../../Entities/JobDescription";
import { useSampleQuestions as useSampleQuestionsContext } from "../../../../../../../Context/SampleQuestionsContext";
import { RoundFormValues, UseSampleQuestionsReturn } from "../types";
import {
  logError,
  getUserFriendlyErrorMessage,
} from "../../../../../utils/errorHandling";

interface UseSampleQuestionsProps {
  form: FormInstance<RoundFormValues>;
  formValues: RoundFormValues | undefined;
  requirements: Requirements[];
  editingRound?: InterviewRound | null;
}

export const useSampleQuestionsHook = ({
  form,
  formValues,
  requirements,
  editingRound,
}: UseSampleQuestionsProps): UseSampleQuestionsReturn => {
  const { getSampleQuestionsByRound } = useSampleQuestionsContext();
  
  // Sample Questions state
  const [sampleQuestions, setSampleQuestions] = useState<SampleQuestion[]>([]);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [showSampleQuestions, setShowSampleQuestions] = useState(false);
  const [lastFormSnapshot, setLastFormSnapshot] = useState<string>("");
  const [hasFormChangedAfterGeneration, setHasFormChangedAfterGeneration] = useState(false);

  // Track form changes after questions are generated
  useEffect(() => {
    if (sampleQuestions.length > 0 && lastFormSnapshot) {
      const currentFormSnapshot = JSON.stringify({
        title: formValues?.title || "",
        questionGenerationPrompt: formValues?.questionGenerationPrompt || "",
        questionType: formValues?.questionType || "mcq",
        evaluationCriteria: formValues?.evaluationCriteria || "",
        requirements: requirements || [],
      });
      const hasChanged = lastFormSnapshot !== currentFormSnapshot;
      setHasFormChangedAfterGeneration(hasChanged);
    } else {
      setHasFormChangedAfterGeneration(false);
    }
  }, [formValues, sampleQuestions.length, lastFormSnapshot, requirements]);

  // Check if form fields are filled (at least title and questionGenerationPrompt)
  const isFormFilled = !!(
    formValues?.title?.trim() && 
    formValues?.questionGenerationPrompt?.trim()
  );

  // Convert form values to InterviewRound format
  const getRoundFromFormValues = (): InterviewRound | null => {
    if (!formValues) return null;

    return {
      _id: editingRound?._id,
      title: formValues.title || "",
      description: "",
      questionGenerationPrompt: formValues.questionGenerationPrompt || "",
      evaluationPrompt: formValues.evaluationCriteria || "",
      answerType: formValues.questionType || "mcq",
    };
  };

  // Create a snapshot of current form state for comparison
  const createFormSnapshot = (): string => {
    const currentValues = form.getFieldsValue();
    const snapshot = {
      title: currentValues.title || "",
      questionGenerationPrompt: currentValues.questionGenerationPrompt || "",
      questionType: currentValues.questionType || "mcq",
      evaluationCriteria: currentValues.evaluationCriteria || "",
      requirements: requirements || [],
    };
    return JSON.stringify(snapshot);
  };

  // Fetch sample questions
  const fetchSampleQuestions = async () => {
    if (!isFormFilled || !requirements || requirements.length === 0) {
      message.warning(
        "Please fill in the form fields and ensure requirements are added in Step 2 to view sample questions."
      );
      return;
    }

    const round = getRoundFromFormValues();
    if (!round) {
      message.warning(
        "Please fill in the required form fields to view sample questions."
      );
      return;
    }

    setIsLoadingQuestions(true);
    try {
      const questions = await getSampleQuestionsByRound(round, requirements);
      const currentFormSnapshot = createFormSnapshot();
      setSampleQuestions(questions || []);
      setShowSampleQuestions(true);
      setLastFormSnapshot(currentFormSnapshot);
      setHasFormChangedAfterGeneration(false);
    } catch (error) {
      // Log error with context
      logError("useSampleQuestions.fetchSampleQuestions", error, {
        roundTitle: round?.title,
        hasRequirements: requirements && requirements.length > 0,
      });

      // Show user-friendly error message
      const errorMessage = getUserFriendlyErrorMessage(
        error,
        "Failed to load sample questions. Please try again."
      );
      message.error(errorMessage);
      setSampleQuestions([]);
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  // Reset sample questions
  const resetSampleQuestions = () => {
    setSampleQuestions([]);
    setShowSampleQuestions(false);
    setLastFormSnapshot("");
    setHasFormChangedAfterGeneration(false);
  };

  return {
    sampleQuestions,
    isLoadingQuestions,
    showSampleQuestions,
    hasFormChangedAfterGeneration,
    fetchSampleQuestions,
    resetSampleQuestions,
    setLastFormSnapshot,
  };
};

export default useSampleQuestionsHook;

